import { AlertCircle, Send } from "lucide-react";
import { useId, useState } from "react";

import { IncidentAttachmentsPicker } from "@/modules/dashboard/payment-incidents/components/IncidentAttachmentsPicker";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaInput } from "@/shared/components/forms/ColocaInput";
import { ColocaLabel } from "@/shared/components/forms/ColocaLabel";
import useTranslations from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";

import {
  WALLET_DEBIT_ATTACHMENTS_PER_UPLOAD,
  WALLET_DEBIT_EXTERNAL_REFERENCE_MAX,
} from "../constants/walletDebits.constants";
import { WalletDebitsService } from "../services/walletDebits.service";
import type { WalletDebit } from "../types/walletDebits.types";

/**
 * Records the manual send of a reserved debit: its reference at the bank or the provider and, optionally, the
 * proof. A proof that fails to upload never undoes the send; this form is gone once the debit is sent, so the
 * failure is handed up.
 */
export function WalletDebitSendForm({
  debit,
  onChange,
  onUploadFailed,
}: {
  debit: WalletDebit;
  onChange: (debit: WalletDebit) => void;
  onUploadFailed: (message: string) => void;
}) {
  const { t } = useTranslations("walletDebits");
  const inputId = useId();
  const [reference, setReference] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const trimmed = reference.trim();

  const submit = async () => {
    if (!trimmed || submitting) return;
    setSubmitting(true);
    setError(null);
    let latest: WalletDebit;
    try {
      latest = await WalletDebitsService.markSent(debit.id, trimmed);
    } catch (sendError: unknown) {
      setError(HttpApiService.getErrorMessage(sendError));
      setSubmitting(false);
      return;
    }
    if (files.length) {
      try {
        latest = await WalletDebitsService.uploadAttachments(debit.id, files);
      } catch (uploadError: unknown) {
        onUploadFailed(t("send_upload_failed", { message: HttpApiService.getErrorMessage(uploadError) }));
      }
    }
    setSubmitting(false);
    onChange(latest);
  };

  return (
    <section className="space-y-3 rounded-xl border border-border px-4 py-3">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{t("send_title")}</h3>
        <p className="text-xs text-muted-foreground">{t("send_hint")}</p>
      </div>
      <div className="space-y-1.5">
        <ColocaLabel htmlFor={inputId}>{t("field_external_reference")}</ColocaLabel>
        <ColocaInput
          id={inputId}
          value={reference}
          onValueChange={setReference}
          placeholder={t("field_external_reference_placeholder")}
          maxLength={WALLET_DEBIT_EXTERNAL_REFERENCE_MAX}
          autoComplete="off"
          disabled={submitting}
        />
      </div>
      <div className="space-y-1.5">
        <ColocaLabel>{t("field_attachments")}</ColocaLabel>
        <IncidentAttachmentsPicker
          value={files}
          onChange={setFiles}
          disabled={submitting}
          max={WALLET_DEBIT_ATTACHMENTS_PER_UPLOAD}
        />
      </div>
      {error && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}
      <ColocaButton
        variant="primary"
        icon={Send}
        loading={submitting}
        disabled={!trimmed || submitting}
        onClick={() => void submit()}
      >
        {t("send_action")}
      </ColocaButton>
    </section>
  );
}
